/**
 * Treemapchart Component
 * Renders an interactive treemap chart using ApexCharts library
 * Displays hierarchical data for either population distribution by district or cancer cases by type
 */

import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";

/**
 * Treemapchart component that dynamically generates treemap visualizations
 * @param {Object} chartdata - Data containing district/cancer names and cases count
 * @param {Object} displaychart - Object containing chart display options (type)
 */
const Treemapchart = ({ chartdata, displaychart }) => {
  // Destructure chart data with default empty arrays
  const {
    district_names = [],
    cases_count = [],
    cancer_names = [],
  } = chartdata;
  
  // Extract the display chart type value
  const { value } = displaychart || {};
  
  // State management for treemap chart options and data series
  const [treemapOptions, setTreemapOptions] = useState({});
  const [treemapSeries, setTreemapSeries] = useState([]);
  
  // Determine chart type and corresponding categories for labels
  const chartType = String(value).toLowerCase();
  const categories = chartType === "population" ? district_names : cancer_names;
  // Initialize treemap configuration and data whenever chartdata or display value changes
  useEffect(() => {
    if (value === "population") {
      // Configure treemap for population distribution by district
      setTreemapSeries([
        {
          // Map district names and their corresponding case counts into chart data format
          data: district_names.map((district, index) => ({
            x: district,
            y: cases_count[index] ?? 0,
          })),
        },
      ]);
      setTreemapOptions({
        chart: {
          height: 350,
          type: "treemap",
        },
        plotOptions: {
          treemap: {
            distributed: true, // Each district gets a different color
          },
        },
      });
    } else {
      // Configure treemap for cancer type distribution
      setTreemapOptions({
        chart: {
          height: 350,
          type: "treemap",
        },

        plotOptions: {
          treemap: {
            distributed: true, // Each cancer type gets a different color
          },
        },
      });
      // Map cancer names and their corresponding case counts into chart data format
      setTreemapSeries([
        {
          data: cancer_names.map((cancer, index) => ({
            x: cancer,
            y: cases_count[index] ?? 0,
          })),
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartdata, displaychart]);
  // Display loading spinner if data is not ready or invalid
  if (
    !chartdata ||
    !categories ||
    !Array.isArray(categories) ||
    categories.length === 0
  ) {
    return (
      <div className="d-flex justify-content-center">
        <div className="spinner-border center text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }
  
  // Render the treemap chart with configured options and data series
  return (
    <div className="container">
      <Chart
        options={treemapOptions}
        series={treemapSeries}
        type="treemap"
        width={"75%"}
      />
    </div>
  );
};

// Export the Treemapchart component as the default export
export default Treemapchart;
